import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';

export type TeamNode = {
  team_id: string;
  team_ou: string;
  team_bu: string;
  children: TeamNode[];
};

export type UseTeamTreeResult = {
  tree: TeamNode | undefined;
  loading: boolean;
  error: Error | undefined;
};

export function useTeamTree(teamId: string | undefined): UseTeamTreeResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tree, setTree] = useState<TeamNode | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    if (teamId) {
      setLoading(true);
      setError(undefined);

      (async () => {
        try {
          const baseUrl = await discoveryApi.getBaseUrl('common-utils');
          const response = await fetchApi.fetch(
            `${baseUrl}/team-tree?teamId=${encodeURIComponent(teamId)}`,
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch team tree: ${response.statusText}`);
          }
          const data = await response.json();
          if (!cancelled) setTree(data.tree);
        } catch (e) {
          if (!cancelled)
            setError(e instanceof Error ? e : new Error(String(e)));
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [teamId, discoveryApi, fetchApi]);

  return { tree, loading, error };
}
